import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { calculateDatesDifference } from "../../../utils/calculateDatesDifference";
import { formatDate } from "../../../utils/formatDate";
import { parseISO } from 'date-fns';

export default function CommonPdfExperiencesSection( { resumeData, translations } ) {
    const { experiences } = extractResumeDataDocumentSection( resumeData );
    const { isAnonymous, isAlcyone, documentLanguage } = extractResumeProperties( resumeData );

    const themeColor = isAlcyone ? '#09B4BF' : '#0B86C8';

    function difference( from, to ) {
        const { years, months } = calculateDatesDifference( from, to );
        let str = '';
        if ( years > 0 ) {
            str += `${ years } ${ years > 1 ? translations.general.years : translations.general.year }`;
        }
        if ( months > 0 && years > 0 ) {
            str += ` ${ translations.general.and } `;
        }
        if ( months > 0 ) {
            str += `${ months } ${ months > 1 ? translations.general.months : translations.general.month }`;
        }
        return str;
    }

    const styles = StyleSheet.create( {
        section: {
            flexDirection: 'row',
            fontSize: 8,
        },
        leftColumn: {
            width: '16%',
        },
        rightColumn: {
            width: '85%',
            marginLeft: 6,
        },
        textStart: {
            margin: 0,
            color: "#000000",
            textAlign: "left"
        },
        bold: {
            fontWeight: 'bold',
        },
        textDate: {
            fontSize: 9,
            marginBottom: 0,
        },
        category: {
            fontSize: 8,
            color: themeColor,
            marginBottom: 1,
            marginTop: 2,
            textAlign: 'start',
        },
        client: {
            fontSize: 8,
            color: themeColor,
            marginBottom: 1,
            marginTop: 1,
            textAlign: 'start',
        },
        sectionTitle: {
            fontSize: 12,
            fontWeight: 'bold',
            marginBottom: 2,
            textTransform: 'uppercase',
            color: themeColor,
        },
        titleContainer: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        line: {
            flexGrow: 1,
            height: 1,
            backgroundColor: themeColor,
            marginLeft: 5,
        },
    } );

    return (
        <View style={ { marginTop: 10 } }>
            <View style={ styles.titleContainer }>
                <Text style={ styles.sectionTitle }>
                    { translations.pdf.experiencesTitle }
                </Text>
                <View style={ styles.line }/>
            </View>
            { experiences
                ?.sort((a, b) => parseISO(b?.from) - parseISO(a?.from))
                .filter( ( experience ) => experience )
                .map( ( experience, index ) => {
                    const validRoles = Array.isArray( experience.roles )
                        ? experience.roles.filter( role => role?.text && role.text.trim() !== '' )
                        : [];
                    const diff = difference( experience.from, experience.isCurrent ? null : experience.to );
                    // experience.isCurrentJob
                    // ? calculateDifference(parseISO(experience.dateDebut), new Date())
                    // : calculateDifference(parseISO(experience.dateDebut), parseISO(experience.dateFin));

                    return (
                        <View key={ `experience-${ index }` }
                              style={ { marginVertical: 4 } }
                        >
                            { index > 0 && (
                                <View style={ { height: 1, backgroundColor: themeColor, marginVertical: 2 } }/>
                            ) }
                            <View style={ [
                                styles.section,
                                { margin: 0 }
                            ] }>
                                <View style={ styles.leftColumn }>
                                    <Text style={ [
                                        styles.bold,
                                        styles.textStart,
                                        { fontSize: 8 }
                                    ] }>
                                        { isAnonymous ? translations.pdf.employerLabel : experience.employer }
                                    </Text>
                                    { experience.activitySector?.translations?.[ documentLanguage ] && (
                                        <Text style={ [
                                            styles.bold,
                                            styles.textStart,
                                            { fontSize: 8 }
                                        ] }>
                                            { experience.activitySector.translations[ documentLanguage ] }
                                        </Text>
                                    ) }
                                    <Text>{ experience.location }</Text>
                                    <Text style={ styles.textDate }>
                                        { experience.from && ( experience.to || experience.isCurrent )
                                            ? formatDate( experience.from, documentLanguage ) + ' - ' + ( experience.isCurrent ? translations.pdf.isCurrentLabel : formatDate( experience.to, documentLanguage ) )
                                            : formatDate( experience.from, documentLanguage ) }
                                    </Text>
                                    { diff && <Text style={ styles.textDate }>{ `(${ diff })` }</Text> }
                                </View>
                                <View style={ styles.rightColumn }>
                                    <View style={ { flexDirection: 'row', flexWrap: 'wrap' } }>
                                        <Text style={ [ styles.client, styles.bold ] }>
                                            { experience.jobPosition }
                                            { ' ' }
                                        </Text>
                                        { experience.isConsultingMission && (
                                            <Text style={ [ styles.client, styles.bold ] }>
                                                { ' - ' }
                                                { translations.pdf.isConsultingMissionLabel }
                                                { ' ' }
                                                { isAnonymous && experience.client ? 'Client' : experience.client }
                                            </Text>
                                        ) }
                                    </View>
                                    <Text>
                                        { experience.contractType?.translations?.[ documentLanguage ] }
                                    </Text>
                                    { validRoles.length > 0 && (
                                        <View style={ { width: "95%" } }>
                                            <Text style={ styles.category }>
                                                { translations.pdf.rolesLabel }
                                            </Text>
                                            { validRoles
                                                .sort( ( a, b ) => a.order - b.order )
                                                .map( ( role, roleIndex ) => (
                                                    <View key={ roleIndex }
                                                          style={ { flexDirection: "row", marginBottom: 1 } }>
                                                        <Text style={ { marginHorizontal: 8 } }>•</Text>
                                                        <Text>{ role.text }</Text>
                                                    </View>
                                                ) ) }
                                        </View>
                                    ) }
                                    { experience.context && experience.context.trim() !== "" && (
                                        <>
                                            <Text style={ styles.category }>
                                                { translations.pdf.contextLabel }
                                            </Text>
                                            <Text>{ experience.context }</Text>
                                        </>
                                    ) }
                                    { experience.achievement && experience.achievement.trim() !== "" && (
                                        <>
                                            <Text style={ styles.category }>
                                                { translations.pdf.achievementsLabel }
                                            </Text>
                                            <Text>{ experience.achievement }</Text>
                                        </>
                                    ) }
                                    { Array.isArray( experience.actions )
                                        && experience.actions.some( action => action?.text && action.text.trim() !== '' )
                                        && (
                                        <View style={ { width: "95%" } }>
                                            <Text
                                                style={ styles.category }>
                                                { translations.pdf.actionsLabel }
                                            </Text>
                                            { experience.actions
                                                .filter( action => action?.text && action.text.trim() !== '' )
                                                .sort( ( a, b ) => a.order - b.order )
                                                .map( ( action, actionIndex ) => (
                                                    <View key={ actionIndex }
                                                          style={ { flexDirection: "row", marginBottom: 1 } }>
                                                        <Text style={ { marginHorizontal: 8 } }>•</Text>
                                                        <Text>{ action.text }</Text>
                                                    </View>
                                                ) ) }
                                        </View>
                                    ) }
                                    { experience.technicalEnvironment
                                        && Array.isArray( experience.technicalEnvironment )
                                        && experience.technicalEnvironment.length > 0
                                        && (
                                            <View style={ { marginBottom: 4 } }>
                                                <Text style={ styles.category }>
                                                    { translations.pdf.technicalEnvironmentLabel }
                                                </Text>
                                                <Text>
                                                    { experience.technicalEnvironment
                                                        .map( skill => {
                                                            if ( typeof skill === 'string' ) {
                                                                return skill;
                                                            }
                                                            return skill?.translations?.[ documentLanguage ] || skill;
                                                        } )
                                                        .join( ', ' ) }
                                                </Text>
                                            </View>
                                        ) }
                                </View>
                            </View>
                        </View>
                    );
                } ) }
        </View>
    );
}
